import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

interface PromotionCondition {
  type: 'min_quantity' | 'min_amount' | 'category' | 'specific_items' | 'day_of_week' | 'time_range';
  value: any;
}

interface PromotionDiscount {
  type: 'percentage' | 'fixed_amount' | 'fixed_price' | 'buy_x_get_y';
  value: number;
  maxDiscount?: number;
}

interface Promotion {
  _id: string;
  name: { en: string; th: string };
  description?: { en: string; th: string };
  type: 'discount' | 'bundle' | 'buy_x_get_y' | 'seasonal';
  conditions: PromotionCondition[];
  discount: PromotionDiscount;
  applicableCategories?: string[];
  applicableItems?: string[];
  applicableBranches?: string[];
  startDate: string;
  endDate: string;
  isActive: boolean;
  priority: number;
  autoApply: boolean;
  requiresCouponCode: boolean;
  couponCode?: string;
  usageLimit?: number;
  usageCount: number;
  createdBy: string;
  createdAt: string;
}

// Fetch all promotions
export function usePromotions(branchId?: string, isActive?: boolean) {
  return useQuery({
    queryKey: ['promotions', branchId, isActive],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (isActive !== undefined) params.append('isActive', String(isActive));

      const response = await apiClient.get<APIResponse<{ promotions: Promotion[] }>>(
        `/promotions?${params.toString()}`
      );
      return response.data.data.promotions;
    },
  });
}

// Fetch single promotion
export function usePromotion(id: string) {
  return useQuery({
    queryKey: ['promotion', id],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ promotion: Promotion }>>(
        `/promotions/${id}`
      );
      return response.data.data.promotion;
    },
    enabled: !!id,
  });
}

// Fetch active promotions for POS
export function useActivePromotions(branchId: string) {
  return useQuery({
    queryKey: ['active-promotions', branchId],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ promotions: Promotion[] }>>(
        `/promotions/active/${branchId}`
      );
      return response.data.data.promotions;
    },
    enabled: !!branchId,
  });
}

export type { Promotion, PromotionCondition, PromotionDiscount };
